import BaseApi from '../baseApi';

export default class CustomerApi extends BaseApi {
  constructor(obj) {
    obj = obj || {};
    super(obj);
  }

  add(customer) {
    customer = customer || {};

    return new Promise((resolve, reject) => {
      return this.api
        .post('/customers', customer)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  update(customer) {
    customer = customer || {};

    return new Promise((resolve, reject) => {
      return this.api
        .put(`/customers/${customer.id}`, customer)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  /**
   * Atualiza apenas as naturezas de operação de um banco de câmbio do cliente (edição).
   * @param {number} customerId
   * @param {number} idExchangeBank
   * @param {number} idExchangeType
   * @param {number[]} operationNatureIds
   */
  updateExchangeBank(customerId, idExchangeBank, idExchangeType, operationNatureIds) {
    return this.api.patch(`/customers/${customerId}/exchange-banks`, {
      idExchangeBank,
      idExchangeType,
      operationNatureIds
    });
  }

  findAll(filters) {
    return new Promise((resolve, reject) => {
      return this.api
        .get('/customers', { params: filters })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  findId(id) {
    id = id || {};

    return new Promise((resolve, reject) => {
      return this.api
        .get(`/customers/${id}`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  findByDocument(doc) {
    return new Promise((resolve, reject) => {
      const digits = (doc || '').toString().replace(/[^0-9]/g, '');

      if (!digits) return reject(new Error('Documento inválido'));

      return this.api
        .get(`/customers/by-document/${digits}`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  checkDocumentDateMatch(doc, date) {
    return new Promise((resolve, reject) => {
      const digits = (doc || '').toString().replace(/[^0-9]/g, '');

      if (!digits) return reject(new Error('Documento inválido'));

      if (!date) return reject(new Error('Data inválida'));

      return this.api
        .post(`/customers/check-document-date`, { document: digits, date })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  inactive(customer) {
    customer = customer || {};

    return new Promise((resolve, reject) => {
      return this.api
        .patch(`/customers/${customer.id}`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  checkDocumentExists(document, excludeId) {
    return this.api.post('/customers/check-document', { document, excludeId });
  }

  reactivate(customerId, reactivation) {
    return this.api.patch(`/customers/${customerId}/reactivate`, reactivation);
  }

  updateStatus(customerId, statusUpdateDto) {
    statusUpdateDto = statusUpdateDto || {};

    return new Promise((resolve, reject) => {
      return this.api
        .patch(`/customers/${customerId}/update-status`, statusUpdateDto)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  addLimit(idCustomer, idExchangeBank, limit) {
    const idCustomerStr = idCustomer != null && idCustomer !== '' ? String(idCustomer) : '';
    const idExchangeBankStr = idExchangeBank != null && idExchangeBank !== '' ? String(idExchangeBank) : '';
    const limitPayload = limit || {};

    if (!idCustomerStr || !idExchangeBankStr) {
      return Promise.reject(
        new Error('ID do cliente e ID do banco são obrigatórios para adicionar limite.')
      );
    }

    return new Promise((resolve, reject) => {
      return this.api
        .post(`/customers/${idCustomerStr}/bank/${idExchangeBankStr}/limits`, limitPayload)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  findExchangeBanks(id, idType, activeOnly = false) {
    id = id || {};

    const params = {};

    if (activeOnly) {
      params.activeOnly = 'true';
    }

    return new Promise((resolve, reject) => {
      return this.api
        .get(`/customers/${id}/exchange-type/${idType}/banks`, { params })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  getLimits(filters) {
    return new Promise((resolve, reject) => {
      return this.api
        .get(`/customers/limits/`, { params: filters })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  downloadRegisterForm(customerId, exchangeBankId) {
    return new Promise((resolve, reject) => {
      return this.api
        .get('/customers/' + customerId + '/bank/' + exchangeBankId + '/form/download', {
          responseType: 'blob'
        })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  getStablecoinLimits(customerId) {
    return new Promise((resolve, reject) => {
      return this.api
        .get(`/stablecoin/receivers/customer/${customerId}/limits`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  getStablecoinLimitHistory(customerId, limit = 50) {
    return new Promise((resolve, reject) => {
      return this.api
        .get(`/stablecoin/receivers/customer/${customerId}/limit-history`, { params: { limit } })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  findProcessingV2() {
    return new Promise((resolve, reject) => {
      return this.api
        .get(`/v2/customers/processing`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  findStatusTransitions(customerId) {
    return new Promise((resolve, reject) => {
      return this.api
        .get(`/status-transitions/customer/${customerId}`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  findStatusTransitionsByCustomers(customerIds) {
    return new Promise((resolve, reject) => {
      return this.api
        .post('/status-transitions/customers', { customerIds })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  updateLastTransitionAdditionalInfo(customerId, additionalInfo) {
    return new Promise((resolve, reject) => {
      return this.api
        .patch(`/status-transitions/customer/${customerId}/additional-info`, { additionalInfo })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  generateSelfieToken(customerId) {
    return new Promise((resolve, reject) => {
      return this.api
        .post(`/customers/${customerId}/generate-selfie-token`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  validateStablecoinCustomer(customerId, complementaryInfo = {}) {
    return new Promise((resolve, reject) => {
      return this.api
        .get(`/customers/${customerId}/stablecoin/validation`, {
          params: complementaryInfo
        })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  resendEmailExternalAccess(customerId) {
    return new Promise((resolve, reject) => {
      return this.api
        .post(`/customers/send-external-access-link/${customerId}`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  checkEmailExists(email, excludeId) {
    const encoded = encodeURIComponent(email.trim().toLowerCase());
    const params = excludeId ? { excludeId } : {};

    return this.api.get(`/customers/check-email/${encoded}`, { params });
  }

  createOrUpdateInternationalBank(customerId, bankData) {
    bankData = bankData || {};

    return new Promise((resolve, reject) => {
      return this.api
        .post(`/customers/${customerId}/international-banks`, bankData)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  deleteInternationalBank(customerId, bankId) {
    return new Promise((resolve, reject) => {
      return this.api
        .delete(`/customers/${customerId}/international-banks/${bankId}`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  createOrUpdateNationalBank(customerId, bankData) {
    bankData = bankData || {};

    return new Promise((resolve, reject) => {
      return this.api
        .post(`/customers/${customerId}/national-banks`, bankData)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  deleteNationalBank(customerId, bankId) {
    return new Promise((resolve, reject) => {
      return this.api
        .delete(`/customers/${customerId}/national-banks/${bankId}`)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  toggleNationalBankStatus(customerId, bankId, isActive) {
    return new Promise((resolve, reject) => {
      return this.api
        .patch(`/customers/${customerId}/national-banks/${bankId}/status`, { isActive })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  toggleInternationalBankStatus(customerId, bankId, isActive) {
    return new Promise((resolve, reject) => {
      return this.api
        .patch(`/customers/${customerId}/international-banks/${bankId}/status`, { isActive })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  togglePaymentAccountStatus(customerId, accountId, isActive) {
    return new Promise((resolve, reject) => {
      return this.api
        .patch(`/customers/${customerId}/payment-accounts/${accountId}/status`, { isActive })
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  }

  checkBankReadiness(customerId) {
    return this.api.get(`/customers/${customerId}/bank-readiness`);
  }

  updateExchangeBankWorkflowStatus(customerId, exchangeBankId, workflowStatus) {
    return new Promise((resolve, reject) =>
      this.api
        .patch(`/customers/${customerId}/bank/${exchangeBankId}/update-status`, workflowStatus)
        .then((response) => resolve(response))
        .catch((error) => reject(error))
    );
  }
}
